import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, faCircleNotch, faCheckCircle, 
  faBoxOpen, faTruck, faCheckSquare, faSquare, faUndo, faChevronDown, faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE } from '../apiConfig';
import { useCalculatorData } from './useCalculatorData';

const goldColor = '#c5a059';

const MovingCalculator = ({ onResultChange }) => {
  const { t } = useTranslation();
  const { meta, loading: loadingAssets } = useCalculatorData(3); 
  const { navbars, groups, assets } = meta;
  const [resultData, setResultData] = useState(null); 
  const [step, setStep] = useState(0);
  const [activeTab, setActiveTab] = useState(null); 
  const [searchQuery, setSearchQuery] = useState('');
  const [openGroup, setOpenGroup] = useState(null);
  const [loading, setLoading] = useState(false);

  const [singleAddress, setSingleAddress] = useState(false);
  const [needTruck, setNeedTruck] = useState(true);
  const [needPacking, setNeedPacking] = useState(false); 
  
  const [addrA, setAddrA] = useState({ address: '', floor: '1', elevator: 'none' });
  const [addrB, setAddrB] = useState({ address: '', floor: '1', elevator: 'none' });
  const [selectedItems, setSelectedItems] = useState({});
  const [distance, setDistance] = useState(0); // Պահում ենք կիլոմետրը

  useEffect(() => {
    if (navbars && navbars.length > 0) {
      setActiveTab(navbars[0].id);
    }
  }, [navbars]);
// Հեռավորության հաշվարկ
useEffect(() => {
  const fetchDistance = async () => {
    // Միայն եթե երկու հասցեն էլ կան ու մի հասցեի ռեժիմը միացրած չի
    if (!singleAddress && addrA.address && addrB.address) {
      try {
        // Քանի որ մենք տեքստային հասցե ունենք, նախ պետք է դարձնել կոորդինատ (Geocoding)
        // Այստեղ օգտագործում ենք անվճար Nominatim API
        const getCoords = async (addr) => {
          const res = await axios.get(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addr)}`);
          if (res.data.length > 0) return { lat: res.data[0].lat, lon: res.data[0].lon };
          return null;
        };

        const cA = await getCoords(addrA.address);
        const cB = await getCoords(addrB.address);

        if (cA && cB) {
          // Հաշվում ենք իրական ճանապարհը OSRM-ով
          const routeRes = await axios.get(`https://router.project-osrm.org/route/v1/driving/${cA.lon},${cA.lat};${cB.lon},${cB.lat}?overview=false`);
          if (routeRes.data.code === 'Ok') {
            const km = (routeRes.data.routes[0].distance / 1000).toFixed(1);
            setDistance(parseFloat(km));
          }
        }
      } catch (err) {
        console.error("Distance Error:", err);
      }
    } else {
      setDistance(0);
    }
  };

  // Օգտագործում ենք debounce, որ ամեն տառ գրելիս հարցում չգնա
  const timer = setTimeout(fetchDistance, 1500);
  return () => clearTimeout(timer);
}, [addrA.address, addrB.address, singleAddress]);
  const groupedStructure = useMemo(() => {
    if (!navbars || !groups || !assets) return [];
    
    return navbars.map(nav => ({
      ...nav,
      childGroups: groups
        .filter(g => String(g.navbar_id) === String(nav.id)) 
        .map(g => ({
          ...g,
          displayName: g.group_name || g.name, 
          items: assets.filter(a => String(a.group_id) === String(g.id))
        }))
        .filter(g => g.items.length > 0)
    })).filter(nav => nav.childGroups.length > 0);
  }, [navbars, groups, assets]);

  const visibleContent = useMemo(() => {
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      return groupedStructure.map(nav => ({
        ...nav,
        childGroups: nav.childGroups.map(g => ({
          ...g,
          items: g.items.filter(i => i.name.toLowerCase().includes(lowerQuery))
        })).filter(g => g.items.length > 0)
      })).filter(nav => nav.childGroups.length > 0);
    }
    return groupedStructure.filter(nav => String(nav.id) === String(activeTab));
  }, [searchQuery, activeTab, groupedStructure]);

  const handleUpdate = (id, val) => {
    const num = Math.max(0, parseInt(val) || 0);
    setSelectedItems(prev => ({ ...prev, [id]: num }));
  };

  const calculate = async () => {
    const finalSelections = {};
    Object.entries(selectedItems).forEach(([id, qty]) => {
      if (qty > 0) finalSelections[id] = qty;
    });

    if (Object.keys(finalSelections).length === 0) return alert(t('select_item_alert'));

    setLoading(true);
    try {
    const payload = {
  calculatorId: 3,
  navId: activeTab,
  selections: finalSelections,
  addrA,
  addrB: singleAddress ? null : addrB,
  distance, // <--- Ավելացնում ենք սա
  needTruck,
  needPacking
};

      const res = await axios.post(`${API_BASE}/api/calculators/calculate`, payload);
      const { totalPrice, summary, details, business } = res.data;

      const itemsListStr = Object.entries(finalSelections)
        .map(([id, qty]) => {
          const asset = assets.find(a => String(a.id) === String(id));
          return `• ${asset ? asset.name : t('item')}: ${qty} ${t('pcs')}`;
        }).join('\n');

      const getElText = (el) => t(`elevator_${el}`);

      let addressLine = `📍 A: ${addrA.address || t('not_specified')} (${addrA.floor} ${t('floor')}, ${getElText(addrA.elevator)})`;
      if (!singleAddress && addrB) {
        addressLine += `\n📍 B: ${addrB.address || t('not_specified')} (${addrB.floor} ${t('floor')}, ${getElText(addrB.elevator)})`;
      }

      const truckText = details?.trucks?.length > 0 ? details.trucks.join(' + ') : t('truck');

      const combinedMessage = `
🚚 ${t('moving_title') || 'MOVING'}
${addressLine}
-------------------------
📦 ${t('items_list')}:
${itemsListStr}
-------------------------
🚛 ${t('vehicle')}: ${truckText}
💰 ${t('total_price')}: ${totalPrice.toLocaleString()} ֏
`.trim();

      setResultData({
        price: totalPrice,
        summary: summary,
        fullDetailsText: combinedMessage,
        itemsList: finalSelections
      });

      if (onResultChange) {
        const finalTruckPrice = (business?.truckCost > 0) ? business.truckCost : 5000;
        onResultChange(totalPrice, summary, {
          ...res.data,
          category_name: 'MOVING',
          total_price: totalPrice,
          truck_price: finalTruckPrice,
          truck_quantity: business?.truckQuantity || details?.trucks?.length || 1,
          worker_count: business?.workerCount || 0,
          location: addrA.address || t('not_specified'),
          message: combinedMessage,
          requested_vehicles: (business?.vehicleSlots || []).map(v => ({
              vehicle_type: v.name || t('truck'),
              name: v.name || t('truck'),
              price: v.price > 0 ? v.price : Math.round(finalTruckPrice / (business?.truckQuantity || 1))
          }))
        });
      }
      setStep(1);
    } catch (e) {
      alert(t('error'));
    } finally {
      setLoading(false);
    }
  };
  const getCoordsSmart = async (fullAddress) => {
  if (!fullAddress) return null;

  // 1. Փորձում ենք գտնել ամբողջական հասցեն
  let res = await axios.get(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fullAddress)}`);
  if (res.data.length > 0) return { lat: res.data[0].lat, lon: res.data[0].lon };

  // 2. Եթե չգտավ, մաքրում ենք հասցեն (Fallback)
  // Օրինակ՝ "Աբովյան 22, բն 4" -> դարձնում ենք "Աբովյան, Երևան"
  const parts = fullAddress.split(',');
  if (parts.length > 1) {
    const fallbackAddr = parts[0]; // Վերցնում ենք միայն առաջին մասը (փողոցը)
    res = await axios.get(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fallbackAddr)}`);
    if (res.data.length > 0) return { lat: res.data[0].lat, lon: res.data[0].lon };
  }

  return null;
};

  if (loadingAssets) {
    return (
      <div style={{display:'flex', justifyContent:'center', padding:'100px'}}>
        <FontAwesomeIcon icon={faCircleNotch} spin size="2x" color={goldColor} />
      </div>
    );
  }

  return (
    <div style={s.outerWrapper}>
      <div style={s.container}>
        {step === 0 ? (
          <div style={s.flexBody}>
            <div style={s.header}>
              <h2 style={s.logoText}>LIBER <span style={{ color: goldColor }}>MOVING</span></h2>
            </div>

            <div style={s.body}>
              <div style={s.sectionCard}>
  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
    <span style={{ fontSize: '12px', fontWeight: '700' }}>{t('address_label')}</span>
    <div 
      onClick={() => setSingleAddress(!singleAddress)} 
      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}
    >
      <FontAwesomeIcon icon={singleAddress ? faCheckSquare : faSquare} color={goldColor} />
      <span style={{ fontSize: '11px' }}>{t('single_address')}</span>
    </div>
  </div>

  <div style={{ display: 'flex', gap: '8px' }}>
    <AddressInput t={t} label={t('address_a')} data={addrA} setData={setAddrA} />
    {!singleAddress && <AddressInput t={t} label={t('address_b')} data={addrB} setData={setAddrB} />}
  </div>

  {/* Ավելացնում ենք կիլոմետրի ցուցադրումը */}
  {!singleAddress && (
    <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px' }}>
      <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '700' }}>📏 Հեռավորություն (կմ):</span>
      <input 
        type="number" 
        value={distance} 
        onChange={(e) => setDistance(parseFloat(e.target.value) || 0)}
        style={{
          width: '60px',
          padding: '4px 8px',
          borderRadius: '8px',
          border: `1px solid ${goldColor}`,
          fontSize: '12px',
          fontWeight: '900',
          textAlign: 'center',
          color: goldColor,
          outline: 'none'
        }}
      />
    </div>
  )}
</div>

              <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                <div style={{ ...s.searchBox, flex: 1, marginBottom: 0 }}>
                  <FontAwesomeIcon icon={faSearch} style={s.searchIcon} />
                  <input 
                    placeholder={t('search_placeholder')} 
                    style={s.searchIn} 
                    value={searchQuery} 
                    onChange={e => setSearchQuery(e.target.value)} 
                  />
                </div>
                <div style={{ display: 'flex', gap: '5px' }}>
                  <button 
                    onClick={() => setNeedPacking(!needPacking)} 
                    style={{ ...s.optBtn, background: needPacking ? goldColor : '#f1f5f9', color: needPacking ? '#fff' : '#64748b' }}
                  >
                    <FontAwesomeIcon icon={faBoxOpen} />
                  </button>
                  <button 
                    onClick={() => setNeedTruck(!needTruck)} 
                    style={{ ...s.optBtn, background: needTruck ? goldColor : '#f1f5f9', color: needTruck ? '#fff' : '#64748b' }}
                  >
                    <FontAwesomeIcon icon={faTruck} />
                  </button>
                </div>
              </div>

              {!searchQuery && (
                <div style={s.navbar}>
                  {navbars.map(nav => (
                    <div 
                      key={nav.id} 
                      onClick={() => setActiveTab(nav.id)} 
                      style={{ 
                        ...s.navItem, 
                        color: activeTab === nav.id ? goldColor : '#94a3b8', 
                        borderBottom: activeTab === nav.id ? `3px solid ${goldColor}` : 'none' 
                      }}
                    >
                      {nav.name}
                    </div>
                  ))}
                </div>
              )}

              <div style={s.itemsContainer}>
                {visibleContent.map(nav => nav.childGroups.map(group => (
                  <div key={group.id} style={s.goldGroupCard}>
                    <div 
                      style={s.categoryHeader} 
                      onClick={() => setOpenGroup(openGroup === group.id ? null : group.id)}
                    >
                      <span style={s.catName}>{group.displayName}</span>
                      <FontAwesomeIcon icon={openGroup === group.id ? faChevronUp : faChevronDown} size="xs" color={goldColor} />
                    </div>

                    {(openGroup === group.id || searchQuery) && (
                      <div style={s.universalGrid}>
                        {group.items.map(item => (
                          <div key={item.id} style={s.itemCard}>
                            <span style={{ fontSize: '20px' }}>{item.icon || '📦'}</span>
                            <div style={s.itemName}>{item.name}</div>
                            <div style={s.cardCounter}>
                              <button onClick={() => handleUpdate(item.id, (selectedItems[item.id] || 0) - 1)} style={s.qtyBtn}>-</button>
                              <span style={{ fontSize: '12px', fontWeight: '800', width: '20px', textAlign: 'center' }}>
                                {selectedItems[item.id] || 0}
                              </span>
                              <button onClick={() => handleUpdate(item.id, (selectedItems[item.id] || 0) + 1)} style={s.qtyBtn}>+</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )))}
              </div>

              <button style={s.mainBtn} onClick={calculate} disabled={loading}>
                {loading ? <FontAwesomeIcon icon={faCircleNotch} spin /> : t('calculate_btn')}
              </button>
            </div>
          </div>
        ) : (
          <div style={s.resultBody}>
            <div style={s.successHeader}>
              <FontAwesomeIcon icon={faCheckCircle} size="3x" color="#10b981" />
              <h3 style={s.successTitle}>{t('calculation_ready')}</h3>
            </div>

            <div style={s.finalCard}>
              <div style={s.priceBox}>
                <div style={s.priceText}>
                  {resultData?.price?.toLocaleString()} ֏
                </div>
              </div>

              <div style={s.detailsList}>
                <div style={s.summaryScroll}>
                  {resultData?.fullDetailsText}
                </div>
              </div>
            </div>

            <button style={s.undoBtn} onClick={() => setStep(0)}>
              <FontAwesomeIcon icon={faUndo} /> {t('new_calc_btn')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const AddressInput = ({ label, data, setData, t }) => (
  <div style={{flex:1, background:'#f8fafc', padding:'8px', borderRadius:'12px', border:'1px solid #e2e8f0', boxSizing: 'border-box', overflow: 'hidden'}}>
    <label style={{fontSize:'8px', fontWeight:'800', color:'#94a3b8', textTransform:'uppercase'}}>{label}</label>
    <input placeholder={t('address_placeholder')} value={data.address} onChange={e=>setData({...data, address: e.target.value})} style={{width:'100%', border:'none', background:'transparent', fontSize:'11px', outline:'none', marginTop:'4px'}} />
    <div style={{display:'flex', gap:'4px', marginTop:'5px'}}>
       <input type="number" placeholder={t('floor_label')} value={data.floor} onChange={e=>setData({...data, floor: e.target.value})} style={{width:'28px', fontSize:'10px', padding:'2px', borderRadius:'4px', border:'1px solid #e2e8f0'}} />
       <select value={data.elevator} onChange={e=>setData({...data, elevator: e.target.value})} style={{flex:1, fontSize:'10px', borderRadius:'4px', border:'1px solid #e2e8f0', width: '0', minWidth: '100%'}}>
          <option value="none">{t('elevator_none')}</option>
          <option value="small">{t('elevator_small')}</option>
          <option value="large">{t('elevator_large')}</option>
       </select>
    </div>
  </div>
);

const s = {
  outerWrapper: { 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'flex-start',
    background: '#f1f5f9', 
    minHeight: '100dvh', 
    width: '100%',
    overflowX: 'hidden',
    boxSizing: 'border-box'
  },
  container: { 
    width: '100%', 
    maxWidth: window.innerWidth <= 480 ? '100%' : '450px', 
    height: window.innerWidth <= 480 ? '100dvh' : 'auto', 
    minHeight: window.innerWidth <= 480 ? '100dvh' : '500px',
    background: '#fff', 
    borderRadius: window.innerWidth <= 480 ? '0px' : '24px', 
    boxShadow: '0 20px 40px rgba(0,0,0,0.1)', 
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxSizing: 'border-box'
  },
  header: { 
    background: '#0f172a', 
    padding: '15px', 
    textAlign: 'center',
    flexShrink: 0 
  },
  logoText: { color: '#fff', margin: 0, fontSize: '14px', fontWeight: '900', letterSpacing: '2px' },
  body: { 
    padding: '10px', 
    display: 'flex', 
    flexDirection: 'column', 
    flex: 1, 
    overflow: 'hidden',
    boxSizing: 'border-box'
  },
  flexBody: { 
    display: 'flex', 
    flexDirection: 'column', 
    height: '100%',
    overflow: 'hidden'
  },
  sectionCard: { 
    background: '#fff', 
    border: '1px solid #e2e8f0', 
    borderRadius: '16px', 
    padding: '10px', 
    marginBottom: '10px',
    flexShrink: 0 
  },
  navbar: { 
    display: 'flex', 
    borderBottom: '1px solid #e2e8f0', 
    marginBottom: '10px', 
    overflowX: 'auto', 
    flexShrink: 0,
    scrollbarWidth: 'none', 
    msOverflowStyle: 'none'
  },
  navItem: { 
    padding: '10px 15px', 
    fontSize: '10px', 
    fontWeight: '800', 
    cursor: 'pointer', 
    textTransform: 'uppercase', 
    whiteSpace: 'nowrap',
    flexShrink: 0 
  },
  searchBox: { position: 'relative', flexShrink: 0 },
  searchIn: { 
    width: '100%', 
    padding: '10px 10px 10px 35px', 
    borderRadius: '10px', 
    border: 'none', 
    background: '#f1f5f9', 
    fontSize: '12px',
    boxSizing: 'border-box' 
  },
  searchIcon: { position: 'absolute', left: '12px', top: '12px', color: '#94a3b8' },
  optBtn: {
    width: '38px',
    height: '38px',
    borderRadius: '10px',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  itemsContainer: { 
    flex: 1, 
    overflowY: 'auto', 
    paddingRight: '2px',
    WebkitOverflowScrolling: 'touch' 
  },
  goldGroupCard: { marginBottom: '10px', background: '#fffcf5', borderRadius: '14px', border: `1px solid ${goldColor}33` },
  categoryHeader: { display: 'flex', justifyContent: 'space-between', padding: '12px', cursor: 'pointer', alignItems: 'center' },
  catName: { fontWeight: '800', fontSize: '12px', color: '#1e293b' },
  universalGrid: { 
    display: 'grid', 
    gridTemplateColumns: window.innerWidth <= 480 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', 
    gap: '8px', 
    padding: '8px', 
    background: '#fff' 
  },
  itemCard: { 
    border: '1px solid #f1f5f9', 
    borderRadius: '12px', 
    padding: '10px 5px', 
    textAlign: 'center', 
    display: 'flex', 
    flexDirection: 'column', 
    alignItems: 'center', 
    justifyContent: 'space-between',
    minHeight: '130px',
    boxSizing: 'border-box'
  },
  itemName: { fontSize: '10px', fontWeight: '700', color: '#475569', margin: '4px 0', minHeight: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: '1.1' },
  cardCounter: { display: 'flex', alignItems: 'center', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0', width: '100%', justifyContent: 'space-between' },
  qtyBtn: { border: 'none', background: 'none', fontWeight: 'bold', color: goldColor, padding: '5px 8px', cursor: 'pointer', fontSize: '16px' },
  mainBtn: { 
    width: '100%', 
    padding: '15px', 
    borderRadius: '14px', 
    border: 'none', 
    background: '#0f172a', 
    color: '#fff', 
    fontWeight: '900', 
    fontSize: '13px', 
    cursor: 'pointer',
    flexShrink: 0,
    marginTop: '8px' 
  },
  resultBody: { 
    padding: '15px', 
    display: 'flex', 
    flexDirection: 'column', 
    height: 'auto', 
    minHeight: window.innerWidth <= 480 ? '100dvh' : 'auto',
    boxSizing: 'border-box' 
  },
  successHeader: { textAlign: 'center', marginBottom: '15px' },
  successTitle: { fontSize: '18px', fontWeight: '800', marginTop: '10px' },
  finalCard: { 
    background: '#fff', 
    borderRadius: '20px', 
    border: '1px solid #e2e8f0', 
    overflow: 'hidden', 
    marginBottom: '15px', 
    flexShrink: 0,
    width: '100%',
    boxSizing: 'border-box'
  },
  priceBox: { background: '#0f172a', color: '#fff', padding: '15px', textAlign: 'center' },
  priceText: { fontSize: '24px', fontWeight: '900', color: '#10b981' },
  detailsList: { padding: '12px', maxHeight: '300px', overflowY: 'auto' },
  summaryScroll: { 
    fontSize: '12px', 
    color: '#1e293b', 
    lineHeight: '1.5', 
    whiteSpace: 'pre-line',
    textAlign: 'left'
  },
  undoBtn: { 
    width: '100%', 
    padding: '15px', 
    borderRadius: '14px', 
    border: '1px solid #e2e8f0', 
    background: '#fff', 
    color: '#64748b', 
    fontWeight: '700', 
    cursor: 'pointer', 
    marginTop: 'auto' 
  }
};

export default MovingCalculator;